import type { ProductCustomization } from '@/features/customization/domain/models/ProductCustomization';
import type { ProductDetail, StoneSpecification } from '@/features/pdp/domain/models/ProductDetail';
import { formatPdpCarat } from '@/features/pdp/utils/formatPdpCarat';

export type { StoneSpecification };

export interface GemstoneLine {
  type: string;
  count?: number;
}

export interface StoneSpecificationGroup {
  type: string;
  specs: StoneSpecification[];
}

export interface ProductStoneDetailsInput {
  diamondCarat: number | null;
  noOfDiamonds: number | null;
  diamondStoneType: string | null;
  solitaireCarat: number | null;
  solitaireClarity: string | null;
  solitaireColor: string | null;
  solitaireTotalNoOfStones: number | null;
  solitaireShape: string | null;
  solitaireCutPolishSymmetry: string | null;
  solitaireFluorescence: string | null;
  gemstones: string | null;
  gemstonesTypeAndCount: string | null;
  stoneSpecifications: StoneSpecification[];
}

function normalizeStoneTypeKey(stoneType: string): string {
  return stoneType.trim().toLowerCase();
}

export function getDiamondStoneTypeFromSpecs(
  specs: StoneSpecification[] | null | undefined
): string | null {
  if (!specs?.length) return null;

  return (
    specs.find((spec) => spec.totalWeight != null && spec.stoneType.trim())?.stoneType.trim() ??
    null
  );
}

function matchesStoneType(stoneType: string, referenceStoneType: string): boolean {
  return normalizeStoneTypeKey(stoneType) === normalizeStoneTypeKey(referenceStoneType);
}

export function isDiamondSpecification(
  spec: StoneSpecification,
  specs: StoneSpecification[] | null | undefined
): boolean {
  const diamondStoneType = getDiamondStoneTypeFromSpecs(specs);
  if (!diamondStoneType) return false;
  return matchesStoneType(spec.stoneType, diamondStoneType);
}

export function groupStoneSpecifications(
  specs: StoneSpecification[] | null | undefined
): StoneSpecificationGroup[] {
  if (!specs?.length) return [];

  const groups: StoneSpecificationGroup[] = [];
  const indexByType = new Map<string, number>();

  for (const spec of specs) {
    const key = normalizeStoneTypeKey(spec.stoneType);
    if (!key) continue;

    const existingIndex = indexByType.get(key);
    if (existingIndex == null) {
      indexByType.set(key, groups.length);
      groups.push({ type: spec.stoneType.trim(), specs: [spec] });
      continue;
    }

    groups[existingIndex].specs.push(spec);
  }

  return groups;
}

export function getDiamondSpecifications(
  specs: StoneSpecification[] | null | undefined
): StoneSpecification[] {
  const diamondStoneType = getDiamondStoneTypeFromSpecs(specs);
  if (!diamondStoneType || !specs?.length) return [];

  return specs.filter((spec) => matchesStoneType(spec.stoneType, diamondStoneType));
}

export function getGemstoneSpecificationGroups(
  specs: StoneSpecification[] | null | undefined
): StoneSpecificationGroup[] {
  const diamondStoneType = getDiamondStoneTypeFromSpecs(specs);
  const gemstoneSpecs = specs?.filter(
    (spec) => !diamondStoneType || !matchesStoneType(spec.stoneType, diamondStoneType)
  );

  return groupStoneSpecifications(gemstoneSpecs);
}

export function formatStoneSpecSize(spec: StoneSpecification): string | null {
  if (spec.size) return spec.size;
  if (spec.totalWeight == null) return null;
  return `${formatPdpCarat(spec.totalWeight)} carats`;
}

type GemstoneSource = string | Array<{ type?: string; count?: number }> | null | undefined;

export function parseProductGemstones(...sources: GemstoneSource[]): GemstoneLine[] {
  for (const raw of sources) {
    if (!raw) continue;

    if (Array.isArray(raw)) {
      const lines = raw
        .filter((item) => Boolean(item?.type))
        .map((item) => ({ type: item.type!, count: item.count }));
      if (lines.length > 0) {
        return lines;
      }
      continue;
    }

    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw) as Array<{ type?: string; count?: number }>;
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed
            .filter((item) => Boolean(item.type))
            .map((item) => ({ type: item.type!, count: item.count }));
        }
      } catch {
        // try next source
      }
    }
  }

  return [];
}

export function buildProductStoneDetailsInput(
  productDetail: ProductDetail,
  customization: ProductCustomization | null,
  diamondStoneType: string | null
): ProductStoneDetailsInput {
  return {
    diamondCarat: customization?.diamondCarat ?? productDetail.diamondCarat,
    noOfDiamonds: customization?.noOfDiamonds ?? productDetail.noOfDiamonds,
    diamondStoneType:
      customization?.diamondStoneType ?? diamondStoneType ?? productDetail.diamondStoneType,
    solitaireCarat: productDetail.solitaireCarat,
    solitaireClarity: productDetail.solitaireClarity,
    solitaireColor: productDetail.solitaireColor,
    solitaireTotalNoOfStones: productDetail.solitaireTotalNoOfStones,
    solitaireShape: productDetail.solitaireShape,
    solitaireCutPolishSymmetry: productDetail.solitaireCutPolishSymmetry,
    solitaireFluorescence: productDetail.solitaireFluorescence,
    gemstones: customization?.gemstonesTypeAndCount ?? productDetail.gemstones,
    gemstonesTypeAndCount:
      customization?.gemstonesTypeAndCount ?? productDetail.gemstonesTypeAndCount,
    stoneSpecifications: productDetail.stoneSpecifications ?? [],
  };
}

export function getGemstoneLines(input: ProductStoneDetailsInput): GemstoneLine[] {
  return parseProductGemstones(input.gemstonesTypeAndCount, input.gemstones);
}

export function getGemstoneDetailSections(input: ProductStoneDetailsInput): Array<{
  type: string;
  gem?: GemstoneLine;
  specs?: StoneSpecification[];
}> {
  const specGroups = getGemstoneSpecificationGroups(input.stoneSpecifications);
  const specTypeKeys = new Set(specGroups.map((group) => normalizeStoneTypeKey(group.type)));
  const fallbackLines = getGemstoneLines(input).filter(
    (gem) => !specTypeKeys.has(normalizeStoneTypeKey(gem.type))
  );

  return [
    ...specGroups.map((group) => ({ type: group.type, specs: group.specs })),
    ...fallbackLines.map((gem) => ({ type: gem.type, gem })),
  ];
}

export function hasDiamondDetails(input: ProductStoneDetailsInput): boolean {
  return Boolean(input.diamondCarat) || getDiamondSpecifications(input.stoneSpecifications).length > 0;
}

export function hasSolitaireDetails(input: ProductStoneDetailsInput): boolean {
  return Boolean(input.solitaireCarat);
}

export function hasGemstoneDetails(input: ProductStoneDetailsInput): boolean {
  return getGemstoneDetailSections(input).length > 0;
}

export function getDiamondSummaryLabel(input: ProductStoneDetailsInput): string | null {
  if (!hasDiamondDetails(input)) return null;

  const parts = [input.noOfDiamonds, input.diamondStoneType, 'Diamonds'].filter(
    (part) => part != null && part !== ''
  );

  return parts.length > 0 ? parts.join(' ') : 'Diamonds';
}

export function getStoneSpecTileLabel(spec: StoneSpecification): string {
  const parts = [spec.count, spec.shape, spec.settingType, spec.stoneType].filter(
    (part) => part != null && part !== ''
  );

  return parts.join(' ');
}

export function getSolitaireTileLabel(
  input: Pick<
    ProductStoneDetailsInput,
    'solitaireTotalNoOfStones' | 'solitaireShape' | 'solitaireClarity' | 'solitaireColor'
  >
): string {
  const parts = [
    input.solitaireTotalNoOfStones,
    input.solitaireShape,
    input.solitaireClarity,
    input.solitaireColor,
    'Solitaire',
  ].filter((part) => part != null && part !== '');

  return parts.length > 0 ? parts.join(' ') : 'Solitaire';
}

export function getSolitaireSummaryLabel(input: ProductStoneDetailsInput): string | null {
  if (!hasSolitaireDetails(input)) return null;

  const parts = [
    input.solitaireTotalNoOfStones,
    input.solitaireClarity,
    input.solitaireColor,
    'Solitaires',
  ].filter((part) => part != null && part !== '');

  return parts.length > 0 ? parts.join(' ') : 'Solitaires';
}
